import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';

import { observability, ProjectSettings } from '@/shared/lib';

import { AuthService } from './generated/auth-service';
import { allClients, authClient, chatClient } from './generated/clients';

type RetryableRequestOptions = {
  [refreshedRequest]: true;
  [retryResponseCapture]: { response: Response | null };
};

const refreshedRequest = Symbol('refreshed-request');
const retryResponseCapture = Symbol('retry-response-capture');

let refreshPromise: Promise<unknown> | null = null;

enum IgnorePaths {
  ME = '/api/v1/auth/me',
  REFRESH = '/auth/refresh',
}

const ignorePaths: Readonly<string[]> = [IgnorePaths.REFRESH] as const;

const isResponseNeedRefresh = (response: Response, request: Request): boolean =>
  response.status === 401 && !ignorePaths.some((el) => request.url.includes(el));

const isRequestRetriedAfterRefresh = (options: object): boolean => refreshedRequest in options;

const markRequestRetriedAfterRefresh = <TOptions extends object>(
  options: TOptions,
  responseCapture: { response: Response | null },
): TOptions & RetryableRequestOptions => ({
  ...options,
  [refreshedRequest]: true,
  [retryResponseCapture]: responseCapture,
});

const captureRetryResponse = (options: object, response: Response): void => {
  if (retryResponseCapture in options) {
    (options as RetryableRequestOptions)[retryResponseCapture].response = response.clone();
  }
};

const camelCaseJsonResponse = async (response: Response): Promise<Response> => {
  const contentType = response.headers.get('content-type');
  if (
    !contentType?.includes('json') ||
    response.status === 204 ||
    response.status === 205 ||
    response.status === 304
  ) {
    return response;
  }

  try {
    const payload: unknown = await response.clone().json();
    if (payload === null || typeof payload !== 'object') {
      return response;
    }

    const transformed = camelcaseKeys(
      payload as Record<string, unknown> | Record<string, unknown>[],
      { deep: true },
    );
    const headers = new Headers(response.headers);
    headers.delete('content-length');
    headers.delete('content-encoding');

    return new Response(JSON.stringify(transformed), {
      status: response.status,
      statusText: response.statusText,
      headers,
    });
  } catch {
    return response;
  }
};

const setInterceptorsToAPI = () => {
  allClients.forEach((client) => {
    client.setConfig({
      ...(client === authClient ? { baseUrl: ProjectSettings.auth.serviceUrl } : {}),
      ...(client === chatClient ? { baseUrl: ProjectSettings.chat.serviceUrl } : {}),
      credentials: 'include',

      bodySerializer: (body) => {
        if (body && typeof body === 'object' && !(body instanceof FormData)) {
          const transformed = snakecaseKeys(body as Record<any, any>, { deep: true });
          return JSON.stringify(transformed);
        }

        return body;
      },

      querySerializer: (query) => {
        const transformed = snakecaseKeys(query, { deep: true });
        return new URLSearchParams(transformed as any).toString();
      },
    });

    client.interceptors.response.use(camelCaseJsonResponse);

    client.interceptors.response.use(async (response, request, options) => {
      if (response.status >= 500) {
        observability.captureMessage('frontend-api-error', {
          level: 'error',
          tags: {
            status: String(response.status),
          },
          extra: {
            url: request.url,
            method: request.method,
          },
        });
      }

      if (isResponseNeedRefresh(response, request)) {
        if (isRequestRetriedAfterRefresh(options)) {
          captureRetryResponse(options, response);
          observability.captureException(new Error('frontend-auth-refresh-retry-failed'));

          return response;
        }

        try {
          if (!refreshPromise) {
            refreshPromise = AuthService.refresh({ throwOnError: true });
          }

          await refreshPromise;
          refreshPromise = null;

          if (!options.method) {
            return response;
          }

          const responseCapture: { response: Response | null } = { response: null };
          const retryOptions = markRequestRetriedAfterRefresh(
            {
              ...options,
              method: options.method,
            },
            responseCapture,
          );
          const result = await client.request(retryOptions);
          return responseCapture.response ?? result.response ?? response;
        } catch {
          observability.captureException(new Error('frontend-auth-refresh-failed'));

          refreshPromise = null;
        }
      }

      captureRetryResponse(options, response);

      return response;
    });
  });
};

export { setInterceptorsToAPI };
